import React, { useState, useEffect } from 'react';
import { FaPlus, FaUsers } from 'react-icons/fa';
import { toast } from 'react-toastify';
import { getClientsByLocal } from '../../../../../../crud/clients_api/local_clients';
import { ClientLocal } from '../../types/client.types';
import LoadingSpinner from '../../../../../../components/common/LoadingSpinner';
import TableSearchBar from '../../../../../../components/common/Table/TableSearchBar';
import ClientsTable from './components/ClientsTable/ClientsTable';
import AddClient from './components/AddClient/AddClient';
import './ClientList.scss';

const ClientList: React.FC = () => {
  const [clients, setClients] = useState<ClientLocal[]>([]);
  const [filteredClients, setFilteredClients] = useState<ClientLocal[]>([]);
  const [loading, setLoading] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);

  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const localId = Number(user.local_id) || 0;

  const fetchClients = async () => {
    setLoading(true);
    try {
      const response = await getClientsByLocal(localId);
      // Sort by last_visit (most recent first)
      const sortedClients = (response.data || []).sort((a, b) => {
        if (!a.last_visit) return 1;
        if (!b.last_visit) return -1;
        return new Date(b.last_visit).getTime() - new Date(a.last_visit).getTime();
      });
      setClients(sortedClients);
      setFilteredClients(sortedClients);
    } catch (error: any) {
      console.error('Error fetching clients:', error);
      toast.error('Error al cargar los clientes');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (localId) {
      fetchClients();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [localId]);

  const handleAddSuccess = (newClient?: ClientLocal) => {
    setShowAddModal(false);
    
    // If we received the new client data, add it directly to the list
    if (newClient) {
      const updatedClients = [newClient, ...clients].sort((a, b) => {
        if (!a.last_visit) return 1;
        if (!b.last_visit) return -1;
        return new Date(b.last_visit).getTime() - new Date(a.last_visit).getTime();
      });
      setClients(updatedClients);
      setFilteredClients(updatedClients);
    } else {
      // Fallback to refetching if no data was returned
      fetchClients();
    }
  };

  const renderEmptyState = () => (
    <div className="empty-state">
      <div className="empty-content">
        <FaUsers className="empty-icon" />
        <h3>¡Agrega tu primer cliente!</h3>
        <p>
          Comienza a construir tu base de clientes leales y rastrea su progreso en el programa de fidelización.
        </p>
        <button
          className="create-button aesthetic"
          onClick={() => setShowAddModal(true)}
        >
          <FaPlus />
          Agregar cliente
        </button>
      </div>
    </div>
  );

  if (loading) {
    return <LoadingSpinner text="Cargando clientes..." transparent />;
  }

  return (
    <div className="client-list-container">
      {clients.length > 0 ? (
        <>
          <div className="client-list-header">
            <div className="clients-search-container">
              <TableSearchBar
                data={clients}
                updateFilteredData={setFilteredClients}
                searchFields={['username', 'last_name', 'email', 'phone_number']}
                placeholder="Buscar clientes por nombre, email o teléfono..."
              />
            </div>
            <button
              className="create-button"
              onClick={() => setShowAddModal(true)}
            >
              <FaPlus />
              Agregar cliente
            </button>
          </div>

          <ClientsTable clients={filteredClients} onRefresh={fetchClients} />
        </>
      ) : (
        renderEmptyState()
      )}

      {/* Add Client Modal */}
      {showAddModal && (
        <AddClient
          show={showAddModal}
          handleClose={() => setShowAddModal(false)}
          onSuccess={handleAddSuccess}
          localId={localId}
        />
      )}
    </div>
  );
};

export default ClientList;